import 'react-circular-progressbar/dist/styles.css';

import { ChevronRight, Loader2, Search, User } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';
import { useNavigate, useSearchParams } from 'react-router-dom';

import EmptyAvatar from '@/assets/icons/empty-avatar.svg?react';
import AddLeadDialog from '@/components/dialogs/AddLeadDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Pagination from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/hooks/ui/use-toast';
import { useGetConsultantDiscoveryData } from '@/hooks/zee/useGetZeeDiscoveryData';
import ClosedTab from '@/pages/dashboard/data-center/ClosedTab.tsx';
import LeadsTab from '@/pages/dashboard/data-center/LeadsTab';
import {
  DiscoveryStage,
  DiscoveryStageItem,
  ZeeUser,
} from '@/types/discovery.type';

const resolveDisplayValue = (
  value: string | DiscoveryStage | DiscoveryStageItem | null | undefined
): string => {
  if (!value) return 'N/A';
  if (typeof value === 'string') return value || 'N/A';
  return value.item_display || value.item_title || 'N/A';
};

const DISCOVERY_LIMIT_STORAGE_KEY = 'consultant_discovery_tab_page_limit';
const DISCOVERY_LIMIT_DEFAULT = 20;

const getStoredLimit = (): number => {
  try {
    const stored = localStorage.getItem(DISCOVERY_LIMIT_STORAGE_KEY);
    const parsed = stored ? parseInt(stored, 10) : NaN;
    return Number.isFinite(parsed) && parsed > 0
      ? parsed
      : DISCOVERY_LIMIT_DEFAULT;
  } catch {
    return DISCOVERY_LIMIT_DEFAULT;
  }
};

const DISCOVERY_SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'lead-source', label: 'Lead Source' },
  { value: 'stage', label: 'Current Stage' },
  { value: 'discovery-completion', label: 'Discovery Completion' },
  { value: 'compatibility-rating', label: 'Compatibility Rating' },
];

export const DashboardDataCenter = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const pageFromUrl = useMemo(
    () => Math.max(1, parseInt(searchParams.get('dpage') || '1', 10)),
    [searchParams]
  );
  const limitFromUrl = useMemo(() => {
    const urlLimit = searchParams.get('dlimit');
    if (urlLimit) {
      return Math.max(1, parseInt(urlLimit, 10));
    }
    return getStoredLimit();
  }, [searchParams]);

  const [page, setPage] = useState(pageFromUrl);
  const [limit, setLimit] = useState(limitFromUrl);
  const [sort, setSort] = useState('stage');
  const [searchVal, setSearchVal] = useState('');
  const [isAddLeadDialogOpen, setIsAddLeadDialogOpen] = useState(false);

  const { result, isLoading, isError, isRefetching } =
    useGetConsultantDiscoveryData({ page, limit });

  const getCompatibilityRating = (user: ZeeUser): number | null => {
    return user.brand_match_score;
  };

  const zeeUsers = result?.data?.zee_users || [];
  const paginationTotal = result?.data?.total ?? 0;
  const paginationTotalPages = result?.data?.total_pages ?? 1;

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setSearchParams((prev) => {
      prev.set('dpage', String(newPage));
      return prev;
    });
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
    try {
      localStorage.setItem(DISCOVERY_LIMIT_STORAGE_KEY, String(newLimit));
    } catch {
      // ignore storage errors
    }
    setSearchParams((prev) => {
      prev.set('dlimit', String(newLimit));
      prev.set('dpage', '1');
      return prev;
    });
  };

  const filteredAndSortedUsers = useMemo(() => {
    const searchLower = searchVal.toLowerCase();
    let filtered = zeeUsers.filter(
      (user) =>
        user.full_name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        resolveDisplayValue(user.current_stage)
          .toLowerCase()
          .includes(searchLower) ||
        user.referred_by?.full_name?.toLowerCase().includes(searchLower) ||
        (user.referred_by === null && 'direct'.includes(searchLower))
    );

    filtered = [...filtered].sort((a, b) => {
      switch (sort) {
        case 'stage': {
          const aStage = resolveDisplayValue(a.current_stage);
          const bStage = resolveDisplayValue(b.current_stage);
          return aStage.localeCompare(bStage);
        }
        case 'lead-source': {
          // Sort by lead source: invited users first (by inviter name), then direct users
          const aSource = a.referred_by?.full_name || 'Direct';
          const bSource = b.referred_by?.full_name || 'Direct';

          // Direct users go to the end
          if (aSource === 'Direct' && bSource !== 'Direct') return 1;
          if (aSource !== 'Direct' && bSource === 'Direct') return -1;

          return aSource.localeCompare(bSource);
        }
        case 'discovery-completion':
          return (b.completion_rate || 0) - (a.completion_rate || 0);
        case 'compatibility-rating': {
          const aRating = getCompatibilityRating(a) || 0;
          const bRating = getCompatibilityRating(b) || 0;
          return bRating - aRating;
        }
        default: {
          const aFullName = a.full_name || '';
          const bFullName = b.full_name || '';
          return aFullName.localeCompare(bFullName);
        }
      }
    });

    return filtered;
  }, [searchVal, sort, zeeUsers]);

  const handleGoDetail = (zeeId: string) => {
    navigate(`/dashboard/zee-profile/${zeeId}`);
  };

  const handleSortChange = (value: string) => {
    setSort(value);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchVal(e.target.value);
  };

  const handleAddLeadClick = () => {
    setIsAddLeadDialogOpen(true);
  };

  useEffect(() => {
    if (isError) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again',
        variant: 'destructive',
      });
    }
  }, [isError]);

  const renderTableRow = (user: ZeeUser) => {
    const compatibilityRating = getCompatibilityRating(user);

    return (
      <TableRow key={user.zee_id}>
        {/* Customer */}
        <TableCell className="flex items-center gap-2 px-4">
          <div className="rounded-full bg-muted">
            {user.photo_url ? (
              <img
                src={user.photo_url}
                alt={user.full_name || 'User'}
                className="h-10 w-10 rounded-full"
              />
            ) : (
              <User className="m-2" />
            )}
          </div>
          <div>
            <p className="text-typo-gray-secondary">
              {user.full_name || 'N/A'}
            </p>
            <p className="text-typo-gray-tertiary">{user.email || 'N/A'}</p>
          </div>
        </TableCell>

        {/* Lead Source */}
        <TableCell className="py-4">
          {user.referred_by ? (
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 flex-shrink-0 overflow-hidden rounded-full border">
                {user.referred_by.photo_url ? (
                  <img
                    src={user.referred_by.photo_url}
                    alt={user.referred_by.full_name || 'Referrer'}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <EmptyAvatar className="!h-full !w-full" />
                )}
              </div>
              <p className="text-sm font-medium">
                {user.referred_by.full_name || 'N/A'}
              </p>
            </div>
          ) : (
            <p className="text-sm text-typo-gray-tertiary">Direct</p>
          )}
        </TableCell>

        {/* Current Stage */}
        <TableCell className="space-y-1 px-4">
          <p className="font-medium">
            {resolveDisplayValue(user.current_stage)}
          </p>
          <p className="text-sm text-typo-gray-tertiary">
            {resolveDisplayValue(user.current_stage_item)}
          </p>
        </TableCell>

        {/* Stage Completion */}
        <TableCell className="px-4">
          <div className="flex items-center gap-2">
            <div className="size-8 flex-shrink-0">
              <CircularProgressbar
                value={user.completion_rate || 0}
                strokeWidth={12}
              />
            </div>
            <p className="font-medium">
              {(user.completion_rate || 0).toFixed(1)}%
            </p>
          </div>
        </TableCell>

        {/* Compatibility Rating */}
        <TableCell className="px-4">
          {compatibilityRating !== null ? (
            <div className="flex items-center gap-2">
              <div className="size-8 flex-shrink-0">
                <CircularProgressbar
                  value={compatibilityRating}
                  strokeWidth={12}
                  styles={buildStyles({
                    pathColor: '#9631F5', // Purple color for compatibility rating
                    trailColor: '#E5E7EB',
                  })}
                />
              </div>
              <p className="font-medium">{compatibilityRating}%</p>
            </div>
          ) : (
            <p className="text-sm text-typo-gray-tertiary">Not rated</p>
          )}
        </TableCell>

        {/* View */}
        <TableCell className="px-4 text-right">
          <Button
            size="icon"
            type="button"
            variant="outline"
            onClick={() => handleGoDetail(user.zee_id)}
          >
            <ChevronRight />
          </Button>
        </TableCell>
      </TableRow>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <p className="text-red-500">Failed to load discovery data</p>
          <p className="text-sm text-typo-gray-tertiary">
            Please try again later
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col justify-center md:flex-row md:justify-between">
        <div className="flex w-full flex-col items-center justify-between gap-5 md:flex-row">
          <div className="tour-data-center-header space-y-1">
            <h2 className="as-strong">Data Center</h2>
            <p className="as-body-02 as-strong text-typo-gray-tertiary">
              All the important information will be here
            </p>
          </div>
          <Button
            onClick={handleAddLeadClick}
            className="tour-invite-candidate"
          >
            Add Lead
          </Button>
        </div>
      </div>

      <Tabs defaultValue="leads">
        <TabsList className="mb-4 w-full md:w-fit">
          <TabsTrigger value="leads" className="tour-leads-tab">
            Leads
          </TabsTrigger>
          <TabsTrigger value="discovery">Discovery</TabsTrigger>
          <TabsTrigger value="closed" className="data-center-closed-tab w-full">
            Closed
          </TabsTrigger>
        </TabsList>

        <TabsContent value="leads">
          <LeadsTab />
        </TabsContent>

        <TabsContent value="discovery">
          <div className="rounded-xl border border-alpha-dark-5 bg-muted">
            <div className="as-strong as-title-01 flex items-center gap-2 border-b border-border p-4 text-zinc-700">
              Prospect
              {isRefetching && <Loader2 className="size-4 animate-spin" />}
            </div>
            <div className="m-4 overflow-hidden rounded-xl border border-alpha-dark-5 bg-white">
              <div className="m-4 flex justify-between">
                <div className="relative flex items-center">
                  <span className="absolute left-3 text-gray-500">
                    <Search size={16} />
                  </span>
                  <Input
                    type="text"
                    value={searchVal}
                    className="pl-8"
                    placeholder="Search"
                    onChange={handleSearchChange}
                  />
                </div>
                <div className="flex gap-1">
                  <Select onValueChange={handleSortChange} value={sort}>
                    <SelectTrigger className="min-w-[200px]">
                      <span className="text-typo-gray-tertiary">Sort By :</span>
                      <SelectValue defaultValue={sort} />
                    </SelectTrigger>
                    <SelectContent>
                      {DISCOVERY_SORT_OPTIONS.map((item) => (
                        <SelectItem value={item.value} key={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="m-4 overflow-hidden rounded-xl border border-alpha-dark-5 bg-muted">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="as-title-04 w-[25%] px-4">
                        Customer
                      </TableHead>
                      <TableHead className="as-title-04 w-[20%] px-4">
                        Lead Source
                      </TableHead>
                      <TableHead className="as-title-04 w-[20%] px-4">
                        Current Stage
                      </TableHead>
                      <TableHead className="as-title-04 w-[15%] px-4">
                        Stage Completion
                      </TableHead>
                      <TableHead className="as-title-04 w-[15%] px-4">
                        Compatibility Rating
                      </TableHead>
                      <TableHead className="as-title-04 w-[5%] px-4 text-right">
                        View
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="bg-white text-typo-gray-secondary">
                    {filteredAndSortedUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="py-8 text-center">
                          <p className="text-typo-gray-tertiary">
                            No discovery data found
                          </p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredAndSortedUsers.map(renderTableRow)
                    )}
                  </TableBody>
                </Table>
              </div>
              <div className="border-t border-alpha-dark-5 px-4 py-3">
                <Pagination
                  page={page}
                  totalPages={paginationTotalPages}
                  total={paginationTotal}
                  limit={limit}
                  onPageChange={handlePageChange}
                  onLimitChange={handleLimitChange}
                />
              </div>
            </div>
          </div>
        </TabsContent>
        <TabsContent
          value="closed"
          className="data-center-closed-content rounded-xl border border-alpha-dark-5 bg-muted"
        >
          <ClosedTab />
        </TabsContent>
      </Tabs>
      <AddLeadDialog
        open={isAddLeadDialogOpen}
        setOpen={setIsAddLeadDialogOpen}
      />
    </div>
  );
};
